import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { translateFn } from '../../../utils/translator.helper';
import { ButtonColors, ButtonTypes } from '../../../ui/gzButton/types';
import { GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { customElement, property, query } from 'lit/decorators.js';
import { WEB_COMPONENT_NETWORK_POP_UP_FOOTER } from '../../../webcomponents.constants';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { map } from 'lit/directives/map.js';

/**
 * This interface is used to define the properties of the footer buttons.
 */
export interface FooterButton {
  id: string;
  color?: ButtonColors;
  disabled?: boolean;
  label?: string;
  type?: ButtonTypes;
  hidden?: boolean;
}

@customElement(WEB_COMPONENT_NETWORK_POP_UP_FOOTER)
export class NetworkPopUpFooterContentWebComponent extends LitElement {
  static styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .action-buttons {
      display: flex;
      justify-content: space-between;
    }
  `;

  @query('gz-button#confirmButton')
  private confirmButton: GzButtonWebComponent;

  @query('gz-button#closeButton')
  private closeButton: GzButtonWebComponent;

  @property({ type: Boolean, reflect: true, attribute: true })
  public confirmButtonDisabled = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  public cancelButtonDisabled = false;

  /**
   * This property is used to define the buttons that will be displayed in the footer.
   */
  private buttons: FooterButton[] = [
    {
      type: ButtonTypes.FULL,
      id: 'confirmButton',
      color: ButtonColors.PRIMARY,
      label: translateFn('Save#preserve state'),
    },
    {
      type: ButtonTypes.OUTLINE,
      id: 'closeButton',
      color: ButtonColors.SECONDARY,
      label: translateFn('Cancel'),
    },
  ];

  /**
   * This method is used to update the properties of a single button.
   * @param id
   * @param updatedProps
   */
  public updateFooterButton(id: string, updatedProps: Partial<FooterButton>) {
    const updatedButton: FooterButton | undefined = this.buttons.find(button => button.id === id);
    if (updatedButton) {
      const { id: string, ...propsToAssign } = updatedProps; // Exclude 'id' from the updatedProps
      Object.assign(updatedButton, propsToAssign);
      this.requestUpdate();
    }
  }

  protected render(): TemplateResult {
    return html`
      <div class="action-buttons d-flex justify-space-between align-items-center" style="height: 100%">
        ${map(this.buttons, (button: FooterButton) => {
          if (button.hidden) {
            return nothing;
          }
          const disabled =
            button.id === 'confirmButton'
              ? button.disabled || this.confirmButtonDisabled
              : this.cancelButtonDisabled || button.disabled;
          const action = button.id === 'confirmButton' ? this.handleConfirmButton : this.handleCancelButton;
          return html`
            <gz-button
              id="${button.id}"
              .color="${button.color}"
              .type="${button.type}"
              .disabled="${disabled}"
              @click="${action}"
              >${button.label}</gz-button
            >
          `;
        })}
      </div>
    `;
  }

  public setConfirmButtonDisabled(value: boolean) {
    if (!this.confirmButton) {
      return;
    }

    this.confirmButton.disabled = value;
  }

  public setCloseButtonDisabled(value: boolean) {
    if (!this.closeButton) {
      return;
    }

    this.closeButton.disabled = value;
  }

  public setConfirmButtonLoadingState(isLoading: boolean) {
    this.confirmButton.setLoadingState(isLoading);
  }

  private handleCancelButton(): void {
    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CANCEL, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleConfirmButton(): void {
    this.confirmButton.setLoadingState(true);
    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CONFIRM, {
        bubbles: true,
        composed: true,
      })
    );
  }
}
